import { Amount } from "@/components/Amount";
import type { CategoryTotal } from "@/api/dashboard";

/**
 * Where the money went, ranked (ADR 0003, `model.md` §4.2).
 *
 * **Every bar is the same colour.** The obvious design — one hue per category —
 * is forbidden by `project.md` §5.5's single accent, and the constraint improves
 * the chart: a reader comparing eight hues is decoding a legend, and a reader
 * comparing eight lengths against a shared baseline is reading the answer.
 *
 * The label and the amount sit with the bar rather than in a legend, so the
 * chart is legible with the bars removed — which is the ADR's test for whether a
 * chart is an ornament or a load-bearing element. Here it is an ornament, and
 * that is deliberate.
 */
export function CategoryBars({
  categories,
  currency,
}: {
  categories: CategoryTotal[];
  currency: string;
}) {
  const largest = Math.max(1, ...categories.map((c) => c.spent_minor));

  return (
    <ul className="space-y-2">
      {categories.map((category) => (
        <li key={category.category_id} data-testid="top-category">
          <div className="flex items-baseline justify-between gap-4 text-sm">
            <span className="truncate">{category.name}</span>
            <Amount
              minor={-category.spent_minor}
              currency={currency}
              testId="top-category-amount"
            />
          </div>
          <div
            className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-track"
            aria-hidden="true"
          >
            {/* One accent colour for every bar; rank is carried by length and
                by the order of the list, never by hue. */}
            <div
              className="h-full rounded-full bg-accent"
              style={{ width: `${(category.spent_minor / largest) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
